/**
 * ServiceFormModal — the ONE add/edit form for the five customer-platform service catalogs.
 * Fields: Main + Sub service name (from the managed dropdown catalog), description AR/EN, active.
 * Names are derived from the picker (sub name, falling back to main). Pricing lives per emirate
 * in <EmiratePricingPanel/> on the manage-service screen.
 */
import { useEffect, useState } from 'react';
import { Loader2, Save, Link2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { MainSubServicePicker } from '../MainSubServicePicker';
import { TranslateButton } from '../TranslateButton';
import { toast } from 'sonner';

const empty = () => ({
  main_service_id: '', sub_service_id: '', main_name_ar: '', main_name_en: '',
  name_ar: '', name_en: '', description_ar: '', description_en: '', is_active: true, icon: 'FileText',
});

export default function ServiceFormModal({ open, onOpenChange, service, onSubmit, saving, language, PageIcon, accentIcon = 'text-[#c9a227]', switchOn = '', testIdPrefix = 'svc' }) {
  const isAr = language === 'ar';
  const [form, setForm] = useState(empty());

  useEffect(() => {
    if (!open) return;
    setForm(service ? { ...empty(), ...pick(service) } : empty());
  }, [open, service]);

  const submit = () => {
    const hasPicker = form.main_service_id && form.sub_service_id;
    const hasLegacyName = form.name_ar.trim() && form.name_en.trim();
    if (!hasPicker && !hasLegacyName) return toast.error(isAr ? 'اختر اسم الخدمة الرئيسي والفرعي من القائمة' : 'Pick the main and sub service name');
    if (!form.description_ar.trim()) return toast.error(isAr ? 'الوصف بالعربية مطلوب' : 'Arabic description is required');
    if (!form.description_en.trim()) return toast.error(isAr ? 'الوصف بالإنجليزية مطلوب' : 'English description is required');
    onSubmit({
      main_service_id: form.main_service_id || '',
      sub_service_id: form.sub_service_id || '',
      main_name_ar: form.main_name_ar || '',
      main_name_en: form.main_name_en || '',
      name_ar: form.name_ar.trim(),
      name_en: form.name_en.trim(),
      description_ar: form.description_ar.trim(),
      description_en: form.description_en.trim(),
      icon: form.icon || 'FileText',
      is_active: !!form.is_active,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1a1a2e] border-[#2a2a4e] text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`${testIdPrefix}-form-modal`}>
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            {PageIcon && <PageIcon className={`h-5 w-5 ${accentIcon}`} />}
            {service ? (isAr ? 'تعديل الخدمة' : 'Edit Service') : (isAr ? 'إضافة خدمة جديدة' : 'Add New Service')}
          </DialogTitle>
          <DialogDescription className="text-[#71717A] text-xs">
            {isAr ? 'اختر اسم الخدمة الرئيسي والفرعي ثم اكتب الوصف. الأسعار والمدة تُضبط لكل إمارة من «إدارة الخدمة».' : 'Pick the main & sub service name and write the description. Prices and durations are set per emirate in “Manage Service”.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="p-3 rounded-lg border border-cyan-500/20 bg-cyan-500/[0.03]">
            <p className="text-[11px] text-cyan-300 mb-2 flex items-center gap-1">
              <Link2 className="h-3 w-3" />
              {isAr ? 'اسم الخدمة الرئيسي والفرعي — من: التحكم بمنصة العملاء ← القوائم المنسدلة ← مسمى الخدمة الرئيسية والفرعية' : 'Main & sub service name — from: Customer Platform Control → Dropdowns → Main & Sub Service Names'}
            </p>
            <MainSubServicePicker
              scope="customer"
              mainId={form.main_service_id}
              subId={form.sub_service_id}
              testIdPrefix={`${testIdPrefix}-picker`}
              onChange={({ mainId, subId, mainAr, mainEn, subAr, subEn }) => setForm((f) => ({
                ...f, main_service_id: mainId, sub_service_id: subId, main_name_ar: mainAr, main_name_en: mainEn,
                name_ar: subAr || mainAr || f.name_ar, name_en: subEn || mainEn || f.name_en,
              }))}
            />
            {(form.name_ar || form.name_en) && (
              <p className="text-[11px] text-white/60 mt-2" data-testid={`${testIdPrefix}-derived-name`}>
                {isAr ? 'اسم الخدمة:' : 'Service name:'} <span className="text-white font-bold">{isAr ? form.name_ar : form.name_en}</span>
                {form.main_name_ar && <span className="text-white/40"> — {isAr ? form.main_name_ar : form.main_name_en}</span>}
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-[#808080]">{isAr ? 'الوصف بالعربية *' : 'Arabic Description *'}</label>
              <TranslateButton sourceText={form.description_en} direction="en_to_ar" onTranslated={(out) => setForm((f) => ({ ...f, description_ar: out }))} disabled={!form.description_en.trim()} testId={`${testIdPrefix}-translate-desc-ar`} size="sm" />
            </div>
            <Textarea value={form.description_ar} onChange={(e) => setForm({ ...form, description_ar: e.target.value })} dir="rtl" rows={3} className="bg-[#0a0a0a] border-[#2a2a4e] text-white" data-testid={`${testIdPrefix}-desc-ar`} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-[#808080]">{isAr ? 'الوصف بالإنجليزية *' : 'English Description *'}</label>
              <TranslateButton sourceText={form.description_ar} direction="ar_to_en" onTranslated={(out) => setForm((f) => ({ ...f, description_en: out }))} disabled={!form.description_ar.trim()} testId={`${testIdPrefix}-translate-desc-en`} size="sm" />
            </div>
            <Textarea value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} dir="ltr" rows={3} className="bg-[#0a0a0a] border-[#2a2a4e] text-white" data-testid={`${testIdPrefix}-desc-en`} />
          </div>

          <div className="flex items-center justify-between bg-[#0a0a0a] rounded-xl p-3 border border-[#2a2a4e]">
            <span className="text-sm text-white">{isAr ? 'خدمة مفعّلة' : 'Service active'}</span>
            <Switch checked={!!form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} className={switchOn} data-testid={`${testIdPrefix}-active-switch`} />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#2a2a4e] text-white" data-testid={`${testIdPrefix}-cancel-btn`}>{isAr ? 'إلغاء' : 'Cancel'}</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#d4af37] hover:bg-[#c9a227] text-black" data-testid={`${testIdPrefix}-save-btn`}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Save className="h-4 w-4 me-2" />}
            {isAr ? 'حفظ' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function pick(s) {
  return {
    main_service_id: s.main_service_id || '', sub_service_id: s.sub_service_id || '',
    main_name_ar: s.main_name_ar || '', main_name_en: s.main_name_en || '',
    name_ar: s.name_ar || '', name_en: s.name_en || '',
    description_ar: s.description_ar || '', description_en: s.description_en || '',
    is_active: s.is_active !== false, icon: s.icon || 'FileText',
  };
}
